import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 150; /* Quick close on mouse leave */

/**
 * A custom element that manages the collections bar and its mega menu interactions.
 * When hovering over a collection in the bar, shows the corresponding mega menu.
 *
 * @extends {Component}
 */
class CollectionsBar extends Component {
  #abortController = new AbortController();
  #activeCollection = /** @type {string | null} */ (null);
  #megaMenuElement = /** @type {HTMLElement | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
    
    this.#findMegaMenu();
    this.#setupEventListeners();
    
    onDocumentLoaded(() => {
      this.#updateHeightVariable();

      window.addEventListener('resize', () => {
        this.#updateHeightVariable();
        this.#updateVisibleMegaMenuHeights();
      }, {
        signal: this.#abortController.signal,
      });

      // In design mode, eagerly load all mega menus so editors can see content
      if (Shopify.designMode) {
        this.querySelectorAll('.header-collections-bar__item--has-children').forEach(
          (item) => this.#ensureMegaMenuLoaded(/** @type {HTMLElement} */ (item))
        );
      }

      // Re-setup listeners after a delay to ensure mega menus are rendered
      setTimeout(() => {
        this.#setupEventListeners();
        this.#setupMegaMenuListeners();
      }, 500);
    });
  }

  /**
   * Update CSS variable for collections bar height (for mega menu positioning)
   */
  #updateHeightVariable() {
    const collectionsBar = /** @type {HTMLElement | null} */ (this.querySelector('.header-collections-bar__nav'));
    if (collectionsBar) {
      const height = collectionsBar.offsetHeight;
      document.documentElement.style.setProperty('--header-collections-bar-height', `${height}px`);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Find the mega menu element that corresponds to collections
   */
  #findMegaMenu() {
    // Find the header-menu component which contains the mega menus
    const headerComponent = document.querySelector('header-component');
    if (!headerComponent) return;

    // Find all header-menu elements
    const headerMenus = headerComponent.querySelectorAll('header-menu');
    if (headerMenus.length > 0 && headerMenus[0]) {
      // Use the first header-menu which should contain the main menu structure
      this.#megaMenuElement = /** @type {HTMLElement} */ (headerMenus[0]);
    }
  }

  /**
   * Setup event listeners for collection links
   */
  #setupEventListeners() {
    const collectionLinks = this.querySelectorAll('.header-collections-bar__link');
    
    collectionLinks.forEach((link) => {
      link.addEventListener('pointerenter', this.#handleCollectionHover, {
        signal: this.#abortController.signal,
      });
      
      link.addEventListener('pointerleave', this.#handleCollectionLeave, {
        signal: this.#abortController.signal,
      });
      
      link.addEventListener('mouseenter', this.#handleCollectionHover, {
        signal: this.#abortController.signal,
      });

      link.addEventListener('focusin', this.#handleCollectionHover, {
        signal: this.#abortController.signal,
      });

      link.addEventListener('mouseleave', this.#handleCollectionLeave, {
        signal: this.#abortController.signal,
      });
    });

    // Also listen for mouse leave on the entire collections bar to close mega menu
    this.addEventListener('pointerleave', this.#handleBarLeave.bind(this), {
      signal: this.#abortController.signal,
    });
  }
  
  /**
   * Setup event listeners for mega menus (called after they're rendered)
   */
  #setupMegaMenuListeners() {
    const megaMenus = this.querySelectorAll('.header-collections-bar__mega-menu');

    megaMenus.forEach((/** @type {HTMLElement} */ megaMenu) => {
      // Use both mouse and pointer events for better compatibility
      const enterHandler = () => {
        // Keep menu open when hovering over it - cancel any pending deactivation
        this.#debouncedDeactivate.cancel();

        // Ensure menu stays visible
        megaMenu.setAttribute('data-visible', 'true');
        const listItem = megaMenu.closest('.header-collections-bar__item');
        const link = listItem?.querySelector('.header-collections-bar__link');
        if (link) {
          link.setAttribute('aria-expanded', 'true');
        }
      };

      const leaveHandler = () => {
        // Start debounced deactivation when leaving mega menu
        this.#debouncedDeactivate();
      };

      megaMenu.addEventListener('mouseenter', enterHandler, {
        signal: this.#abortController.signal,
      });
      megaMenu.addEventListener('pointerenter', enterHandler, {
        signal: this.#abortController.signal,
      });

      megaMenu.addEventListener('mouseleave', leaveHandler, {
        signal: this.#abortController.signal,
      });
      megaMenu.addEventListener('pointerleave', leaveHandler, {
        signal: this.#abortController.signal,
      });
    });
  }

  /**
   * Lazy-load mega menu content from <template> on first interaction
   * @param {HTMLElement} listItem
   */
  #ensureMegaMenuLoaded(listItem) {
    const megaMenu = /** @type {HTMLElement | null} */ (listItem.querySelector('[data-lazy-mega-menu]'));
    if (!megaMenu || megaMenu.children.length > 0) return;

    const itemId = megaMenu.dataset.collectionItemId;
    const template = /** @type {HTMLTemplateElement | null} */ (listItem.querySelector(`template[data-mega-menu-template="${itemId}"]`));
    if (template) {
      megaMenu.appendChild(template.content.cloneNode(true));
      megaMenu.removeAttribute('data-lazy-mega-menu');
      this.#observeMegaMenuItems(megaMenu);
      // Re-setup listeners for newly added mega menu
      this.#setupMegaMenuListeners();
    }
  }

  /**
   * Size each mega menu item's grid row span to its real height.
   * The CSS spans only guess from the number of sub-links, so long labels that wrap
   * (e.g. Hungarian) would overflow into the next item.
   * @param {HTMLElement} megaMenu
   */
  #observeMegaMenuItems(megaMenu) {
    const container = /** @type {HTMLElement | null} */ (megaMenu.querySelector('.mega-menu__items-container'));
    if (!container) return;

    const items = /** @type {NodeListOf<HTMLElement>} */ (container.querySelectorAll('.mega-menu__item'));
    if (items.length === 0) return;

    const observer = new ResizeObserver(() => {
      const styles = getComputedStyle(container);
      const rowSize = parseFloat(styles.getPropertyValue('--collections-mega-menu-row-size'));
      const rowGap = parseFloat(styles.rowGap) || 0;
      const itemGap = parseFloat(styles.getPropertyValue('--collections-mega-menu-item-gap')) || 0;
      if (!rowSize) return;

      items.forEach((item) => {
        const height = item.getBoundingClientRect().height + itemGap;
        const span = Math.ceil((height + rowGap) / (rowSize + rowGap));
        item.style.gridRow = `span ${Math.max(span, 1)}`;
      });
    });

    items.forEach((item) => observer.observe(item));
    this.#abortController.signal.addEventListener('abort', () => observer.disconnect());
  }

  /**
   * Handle hover on a collection link
   * @param {Event} event
   */
  #handleCollectionHover = (event) => {
    const link = /** @type {HTMLElement} */ (event.currentTarget);
    if (!link) return;

    const parentUrl = link.dataset.parentUrl || link.getAttribute('href') || '';
    const hasChildren = link.dataset.hasChildren === 'true';

    // If has children, show collections bar mega menu directly
    if (hasChildren) {
      // Cancel any pending deactivation only for items with children
      this.#debouncedDeactivate.cancel();

      const listItem = link.closest('.header-collections-bar__item');

      // Lazy-load mega menu content on first hover
      if (listItem) this.#ensureMegaMenuLoaded(listItem);

      const megaMenu = /** @type {HTMLElement | null} */ (listItem?.querySelector('.header-collections-bar__mega-menu'));

      if (megaMenu) {
        const wasAlreadyVisible = this.#activeCollection === parentUrl && megaMenu.hasAttribute('data-visible');

        link.setAttribute('aria-expanded', 'true');
        megaMenu.setAttribute('data-visible', 'true');
        this.#activeCollection = parentUrl;
        this.#updateMegaMenuHeight(megaMenu);
        if (!wasAlreadyVisible) window.FabiniCustomEvents?.publishMenuView(link.textContent?.trim() || '');

        // Hide other mega menus
        const allMegaMenus = this.querySelectorAll('.header-collections-bar__mega-menu');
        allMegaMenus.forEach((/** @type {HTMLElement} */ menu) => {
          if (menu !== megaMenu) {
            menu.removeAttribute('data-visible');
            menu.closest('.header-collections-bar__item')?.querySelector('.header-collections-bar__link')?.removeAttribute('aria-expanded');
          }
        });

        return;
      }
    } else {
      // Item without children - immediately close all mega menus
      this.#debouncedDeactivate.cancel();
      this.#deactivate();
      return;
    }

    // Fallback: try to activate main menu item if it exists
    if (!this.#megaMenuElement) return;

    // Try to find corresponding menu item in header-menu
    const menuLinks = this.#megaMenuElement.querySelectorAll('.menu-list__link');

    menuLinks.forEach((/** @type {HTMLElement} */ menuLink) => {
      const href = menuLink.getAttribute('href') || '';
      if (href === parentUrl || (parentUrl && (href.includes(parentUrl) || parentUrl.includes(href)))) {
        const menuListItem = menuLink.closest('.menu-list__list-item');
        if (menuListItem) {
          const menuItemElement = menuListItem.querySelector('[ref="menuitem"]');
          if (menuItemElement && this.#megaMenuElement) {
            const syntheticEvent = {
              target: menuItemElement,
              currentTarget: menuItemElement,
            };
            // @ts-ignore - activate method exists on custom element
            if (typeof this.#megaMenuElement.activate === 'function') {
              // @ts-ignore
              this.#megaMenuElement.activate(syntheticEvent);
            }
            this.#activeCollection = parentUrl;
          }
        }
      }
    });
  };

  /**
   * Update mega menu height for smooth animation
   * @param {HTMLElement} megaMenu
   */
  #updateMegaMenuHeight(megaMenu) {
    if (!megaMenu) return;
    const { top } = megaMenu.getBoundingClientRect();
    const availableHeight = Math.max(0, Math.floor(window.innerHeight - top));
    megaMenu.style.setProperty('--collections-mega-menu-available-height', `${availableHeight}px`);

    const height = Math.min(megaMenu.scrollHeight, availableHeight || megaMenu.offsetHeight);
    megaMenu.style.setProperty('--submenu-height', `${height}px`);
  }

  /**
   * Refresh height constraints for currently open mega menus.
   */
  #updateVisibleMegaMenuHeights() {
    const visibleMegaMenus = this.querySelectorAll('.header-collections-bar__mega-menu[data-visible="true"]');
    visibleMegaMenus.forEach((/** @type {HTMLElement} */ megaMenu) => this.#updateMegaMenuHeight(megaMenu));
  }

  /**
   * Handle leaving a collection link
   * @param {PointerEvent} event
   */
  #handleCollectionLeave = (event) => {
    // Start debounced deactivation
    // If user moves to mega menu, the mega menu's mouseenter will cancel it
    this.#debouncedDeactivate();
  };

  /**
   * Handle leaving the entire collections bar
   */
  #handleBarLeave = () => {
    // Don't deactivate immediately - let the header-menu handle it
    // The mega menu will stay open if hovered, or close after delay
  };

  /**
   * Deactivate the active collection
   */
  #deactivate = () => {
    // Close collections bar mega menus
    const activeLinks = this.querySelectorAll('.header-collections-bar__link[aria-expanded="true"]');
    activeLinks.forEach((link) => {
      link.removeAttribute('aria-expanded');
    });
    
    // Hide all mega menus
    const allMegaMenus = this.querySelectorAll('.header-collections-bar__mega-menu');
    allMegaMenus.forEach((/** @type {HTMLElement} */ megaMenu) => {
      megaMenu.removeAttribute('data-visible');
    });
    
    // Also deactivate main menu if it was activated
    if (this.#megaMenuElement && this.#activeCollection) {
      const menuLinks = this.#megaMenuElement.querySelectorAll('.menu-list__link');
      menuLinks.forEach((/** @type {HTMLElement} */ menuLink) => {
        const href = menuLink.getAttribute('href') || '';
        if (this.#activeCollection && (href === this.#activeCollection || href.includes(this.#activeCollection))) {
          const menuListItem = menuLink.closest('.menu-list__list-item');
          if (menuListItem) {
            const menuItemElement = menuListItem.querySelector('[ref="menuitem"]');
            if (menuItemElement && this.#megaMenuElement) {
              const syntheticEvent = {
                target: menuItemElement,
                currentTarget: menuItemElement,
              };
              // @ts-ignore - deactivate method exists on custom element
              if (typeof this.#megaMenuElement.deactivate === 'function') {
                // @ts-ignore
                this.#megaMenuElement.deactivate(syntheticEvent);
              }
            }
          }
        }
      });
    }
    
    this.#activeCollection = null;
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);
}

if (!customElements.get('collections-bar')) {
  customElements.define('collections-bar', CollectionsBar);
}
